import { Typography } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import CloseIcon from '@material-ui/icons/Close';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import RestoreIcon from '@material-ui/icons/Restore';
import PropTypes from 'prop-types';
import React from 'react';
import { globalStyles } from '../../core/style/gstheme';
import { makeCancelable } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';
import createVisual from './Visual';

const styles = () => {
    return Object.assign({
        visualPanel: {
            display: 'flex',
            flexDirection: 'row-reverse',
            paddingLeft: 12,
            marginLeft: 12,
            marginTop: 12,
            paddingTop: 12,
            height: 'calc(100% - 36px)',
            justifyContent: 'flex-end',
            borderBottomRightRadius: 4,
            boxShadow: '0px 12px 17px #00000024',
            borderRight: 'solid 1px #f0f0f0'
        },
        visualPanelNarrow: {
            width: '44px',
            height: 'calc(100% - 36px)',
            marginLeft: 12,
            marginTop: 12,
            paddingTop: 12,
            borderBottomRightRadius: 4,
            boxShadow: '0px 12px 17px #00000024',
            borderRight: 'solid 1px #f0f0f0'
        }
    }, globalStyles);
};

const HIDDEN = "HIDDEN";
const CHECKED = "CHECKED";
const UNCHECKED = "UNCHECKED";

class VisualisationContainer extends React.Component {

    constructor(props) {
        super(props);
        const propertiesState = {};
        Object.keys(props.objectPropertyConfiguration).forEach(propItem => propertiesState[propItem] = CHECKED);
        this.state = Object.assign({
            initiated: false,
            fullScreen: false,
            objectProperties: Object.keys(props.objectPropertyConfiguration),
            additionalProps: []
        }, propertiesState);
    }

    getActiveProps() {
        const fixedProps = Object.keys(this.props.objectPropertyConfiguration);
        const allProps = fixedProps.concat(this.state.additionalProps);
        const active = allProps.filter(objProp => this.state[objProp] === CHECKED);
        return active;
    }

    componentDidMount() {
        createVisual(
            this._rootNode,
            this.props.sourceNode,
            this.props.graph,
            this.onLoadResource,
            this.getActiveProps(),
            this.props.defaultLang,
            this.props.viewLang,
            this.props.objectPropertyConfiguration
        ).then((visual) => {
            this._visual = visual;
            this.setState({ initiated: true });
        });
    }

    onLoadResource = async (resourceIRI) => {
        await this.loadData([resourceIRI]).promise;
        if (this.cancellable && !this.cancellable.isCancelled()) {
            const data = GS.data(resourceIRI, this.props.graph, false);
            if (data) {
                // http index indicates non-aliased property
                // If properties have just been added they may be stored as strings even for @id properties
                // (because GS SDK is aware it is an object prop)
                const newProps = Object.keys(data)
                    .filter(prop => prop.indexOf("http") === 0
                        && ((Array.isArray(data[prop]) && data[prop].length > 0 &&
                            (data[prop][0].id || data[prop].every(v => v.indexOf("http://") === 0 || v.indexOf("https://") === 0)))
                            || data[prop].id
                            || (typeof data[prop] === 'string' &&
                                (data[prop].indexOf("http://") === 0 || data[prop].indexOf("https://") === 0))));
                let newAdditionalProps = newProps.filter(newProp => !this.state.additionalProps.includes(newProp));
                const updatedProps = this.state.additionalProps.concat(newAdditionalProps);
                let additionalPropertiesState = {};
                newAdditionalProps.forEach(newProp => additionalPropertiesState[newProp] = UNCHECKED);
                this.setState(Object.assign({ additionalProps: updatedProps }, additionalPropertiesState));
            }
        }
    };

    loadData(iris) {
        this.cancellable = makeCancelable(GS.loadData(iris, {}, this.props.graph));
        return this.cancellable;
    }

    componentWillUnmount() {
        this._visual.destroy();
    }

    _setRef(componentNode) {
        this._rootNode = componentNode;
    }

    hidePanel = (hide) => this.setState({ fullScreen: hide });

    reset = async () => {
        let propertiesState = {
            additionalProps: []
        };
        Object.keys(this.props.objectPropertyConfiguration).forEach(propItem => propertiesState[propItem] = CHECKED);
        let additionalPropertiesState = {};
        this.state.additionalProps.forEach(addProp => additionalPropertiesState[addProp] = HIDDEN);
        this.setState(Object.assign(propertiesState, additionalPropertiesState), async () => {
            this._visual.reset(this.getActiveProps());
        });
    };

    handleChange = (event) => {
        const isChecked = event.target.checked ? CHECKED : UNCHECKED;
        this.setState({ [event.target.name]: isChecked }, () => {
            this._visual.update(this.getActiveProps());
        });
    };

    render() {

        const { objectPropertyConfiguration, classes, exitVisualisation } = this.props;
        const { objectProperties, additionalProps, initiated, fullScreen } = this.state;
        const activeAdditionalProperties = additionalProps.filter(addProp => this.state[addProp] !== HIDDEN);

        const controls = <div style={{
            display: 'flex',
            borderLeft: 'solid 1px #f0f0f0',
            width: 30,
            paddingTop: 6,
            paddingLeft: 6,
            paddingRight: 6,
            alignItems: 'space-evenly',
            flexDirection: 'column'
        }}>
            <span style={{ marginBottom: 12 }}>
                <Tooltip placement="right" title='Close visualisation'>
                    <IconButton data-test="close-button" size="small" onClick={exitVisualisation}>
                        <CloseIcon />
                    </IconButton>
                </Tooltip>
            </span>
            <span style={{ marginBottom: 12 }}>
                {fullScreen ?
                    <Tooltip placement="right" title='Show panel'>
                        <IconButton data-test="show-panel" size="small" onClick={() => this.hidePanel(false)}>
                            <FullscreenIcon />
                        </IconButton>
                    </Tooltip>
                    :
                    <Tooltip placement="right" title='Hide panel'>
                        <IconButton data-test="hide-panel" size="small" onClick={() => this.hidePanel(true)}>
                            <FullscreenExitIcon />
                        </IconButton>
                    </Tooltip>
                }
            </span>
            <span style={{ marginBottom: 12 }}>
                <Tooltip placement="right" title='Reset visualisation'>
                    <IconButton data-test="reset-button" size="small" onClick={this.reset}>
                        <RestoreIcon />
                    </IconButton>
                </Tooltip>
            </span>
        </div>;

        return <div style={{
            display: 'flex',
            height: '100%',
            alignItems: 'flex-start'
        }}>
            {initiated &&
                <React.Fragment>
                    {fullScreen ?
                        <div className={classes.visualPanelNarrow}>
                            {controls}
                        </div>
                        :
                        <div className={classes.visualPanel}>
                            {controls}
                            <div style={{
                                overflow: 'hidden'
                            }}>
                                <div style={{ width: '100%' }}>
                                    <img data-test="home" src="/images/graphologi.jpg" alt="Logo" style={{ width: 150 }} />
                                </div>
                                <div style={{
                                    flex: '1',
                                    marginTop: 12,
                                    whiteSpace: 'nowrap',
                                    overflowY: 'scroll',
                                    overflowX: 'hidden',
                                    height: 'calc(100% - 60px)'
                                }}>
                                    <div>
                                        <div data-test="standard-properties" style={{ overflowX: 'hidden' }}>
                                            <Typography className={classes.subHeading}>Standard Properties</Typography>
                                            {objectProperties.map(objProp => {
                                                return <div key={objProp}>
                                                    <Tooltip title={objectPropertyConfiguration[objProp].iri}>
                                                        <FormControlLabel
                                                            data-test="option-label"
                                                            control={
                                                                <Checkbox
                                                                    data-test={"option-" + objProp}
                                                                    checked={this.state[objProp] === CHECKED}
                                                                    onChange={this.handleChange}
                                                                    name={objProp}
                                                                    classes={{ root: classes.compactCheckbox }}
                                                                />
                                                            }
                                                            label={objectPropertyConfiguration[objProp].displayName || objProp}
                                                            classes={{ label: classes.formLabelInlineLightLarge }}
                                                        />
                                                    </Tooltip>
                                                </div>;
                                            })}
                                        </div>
                                        {activeAdditionalProperties && activeAdditionalProperties.length > 0 &&
                                            <div data-test="additional-properties" style={{ marginTop: 12 }}>
                                                <Typography className={classes.subHeading}>Additional Properties</Typography>
                                                {activeAdditionalProperties.map(objProp => {
                                                    const shortName = objProp.includes('#') ?
                                                        decodeURIComponent(objProp.substring(objProp.lastIndexOf('#') + 1))
                                                        :
                                                        decodeURIComponent(objProp.substring(objProp.lastIndexOf('/') + 1));
                                                    return <div key={objProp}>
                                                        <Tooltip title={objProp}>
                                                            <FormControlLabel
                                                                data-test="option-label"
                                                                control={
                                                                    <Checkbox
                                                                        data-test={"option-" + objProp}
                                                                        checked={this.state[objProp] === CHECKED}
                                                                        onChange={this.handleChange}
                                                                        name={objProp}
                                                                        classes={{ root: classes.compactCheckbox }}
                                                                    />
                                                                }
                                                                label={shortName}
                                                                classes={{ label: classes.formLabelInlineLightLarge }}
                                                            />
                                                        </Tooltip>
                                                    </div>;
                                                })}
                                            </div>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                </React.Fragment>
            }
            <div style={{
                width: '100%',
                height: 'calc(100% - 2px)',
            }}>
                <div style={{ zIndex: 2000 }} data-test="visual-tooltip" id="visual-tooltip"></div>
                <svg
                    style={{
                        width: '100%',
                        height: '100%',
                        userSelect: 'none'
                    }}
                    className="visualisation"
                    ref={this._setRef.bind(this)} />
            </div>
        </div>;
    }

}

VisualisationContainer.propTypes = {
    sourceNode: PropTypes.string.isRequired,
    exitVisualisation: PropTypes.func.isRequired,
    graph: PropTypes.string.isRequired,
    objectPropertyConfiguration: PropTypes.object.isRequired,
    displayClasses: PropTypes.object.isRequired,
    defaultLang: PropTypes.string.isRequired,
    viewLang: PropTypes.string.isRequired,
    classes: PropTypes.object
};

export default withStyles(styles)(VisualisationContainer);